import {
  promoteCountForGroup,
  relegateCountForGroup,
  type BoardRow
} from '@h2h/shared';
import { ChevronDown, ChevronUp, Trophy } from '../ui/icons';
import { initials, signedPoints } from '../../lib/format';
import { cn } from '../../lib/cn';
import { zoneForRank, type Zone } from '../../lib/round';
import { teamGradient } from '../../lib/teams';
import { Placeholder } from '../ui/Placeholder';
import { useOpenProfile } from '../../hooks/useOpenProfile';

function MovementBadge({ movement }: { movement: BoardRow['movement'] }) {
  if (movement === 'PROMOTED') {
    return (
      <span className="shrink-0 rounded-[5px] bg-accent-wash px-1.5 py-px text-[9px] font-extrabold uppercase tracking-wide text-accent-ink">
        Up
      </span>
    );
  }
  if (movement === 'RELEGATED') {
    return (
      <span className="shrink-0 rounded-[5px] bg-danger/10 px-1.5 py-px text-[9px] font-extrabold uppercase tracking-wide text-danger">
        Down
      </span>
    );
  }
  return null;
}

function ZoneCutLine({ kind, count }: { kind: 'promotion' | 'relegation'; count: number }) {
  const promote = kind === 'promotion';
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-[6px]',
        promote ? 'bg-accent/[0.08]' : 'bg-danger/[0.06]'
      )}
    >
      <div className={cn('h-px flex-1', promote ? 'bg-accent/25' : 'bg-danger/20')} />
      <span
        className={cn(
          'inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.08em]',
          promote ? 'text-accent-ink' : 'text-danger'
        )}
      >
        {promote ? (
          <ChevronUp width={10} height={10} strokeWidth={3.4} />
        ) : (
          <ChevronDown width={10} height={10} strokeWidth={3.4} />
        )}
        {promote ? `Promotion · top ${count}` : `Relegation · bottom ${count}`}
      </span>
      <div className={cn('h-px flex-1', promote ? 'bg-accent/25' : 'bg-danger/20')} />
    </div>
  );
}

function DivisionTableRow({
  row,
  zone,
  isYou
}: {
  row: BoardRow;
  zone: Zone;
  isYou: boolean;
}) {
  const openProfile = useOpenProfile();
  const promote = zone === 'promotion';
  const relegate = zone === 'relegation';

  return (
    <button
      type="button"
      onClick={() => openProfile(row.userId, isYou)}
      className={cn(
        'tap grid w-full grid-cols-[28px_1fr_auto] items-center gap-2.5 px-3 py-[10px] text-left transition-colors',
        isYou && 'mx-1.5 rounded-[14px] bg-night px-3 py-[11px] shadow-[0_10px_22px_-12px_rgba(0,0,0,0.45)]',
        !isYou && promote && 'bg-accent/[0.05]',
        !isYou && relegate && 'bg-danger/[0.04]',
        !isYou && zone === 'middle' && 'active:bg-fill/70'
      )}
    >
      <span
        className={cn(
          'num text-center text-[13px] font-extrabold',
          isYou && 'text-accent',
          !isYou && promote && 'text-accent-ink',
          !isYou && relegate && 'text-danger',
          !isYou && zone === 'middle' && 'text-ink2'
        )}
      >
        {row.rankInGroup}
      </span>

      <div className="flex min-w-0 items-center gap-2.5">
        <div
          className={cn(
            'grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full text-[10px] font-extrabold text-white',
            isYou && 'bg-accent ring-2 ring-accent ring-offset-1 ring-offset-night'
          )}
          style={isYou ? undefined : { backgroundImage: teamGradient(row.displayName) }}
        >
          {isYou ? (
            <span className="text-[9px] font-extrabold text-white">YOU</span>
          ) : (
            initials(row.displayName)
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className={cn(
                'truncate text-[14px] tracking-snug',
                isYou ? 'font-extrabold text-white' : 'font-semibold text-ink'
              )}
            >
              {isYou ? 'You' : row.displayName}
            </span>
            <MovementBadge movement={row.movement} />
          </div>
          {isYou && (
            <div className="mt-0.5 text-[10px] font-semibold text-white/55">
              {promote ? 'Promotion spot' : relegate ? 'Drop zone' : 'Mid-table'}
            </div>
          )}
        </div>
      </div>

      <span
        className={cn(
          'num min-w-[44px] text-right text-[15px] font-extrabold',
          isYou && 'text-accent',
          !isYou && row.roundScore > 0 && 'text-accent-ink',
          !isYou && row.roundScore < 0 && 'text-danger',
          !isYou && row.roundScore === 0 && 'text-ink'
        )}
      >
        {signedPoints(row.roundScore)}
      </span>
    </button>
  );
}

/**
 * Full division table — every player visible, sorted by rank, with promotion and
 * relegation cut lines. No collapsed middle: you always see the whole group.
 */
export function GroupStandingsBoard({
  standings,
  uid,
  size,
  promoteTarget,
  relegateTarget,
  emptyBody = 'Your group board fills in as fixtures finish and results settle.'
}: {
  standings: BoardRow[];
  uid: string;
  /** Group size the bands scale to (memberIds.length, or standings length if larger). */
  size: number;
  /** e.g. "Silver" — shown in the table legend when promotion is possible. */
  promoteTarget?: string;
  relegateTarget?: string;
  emptyBody?: string;
}) {
  const promote = promoteCountForGroup(size);
  const relegate = relegateCountForGroup(size);
  const rows = [...standings].sort((a, b) => a.rankInGroup - b.rankInGroup);

  if (rows.length === 0) {
    return <Placeholder Icon={Trophy} title="No scores yet" body={emptyBody} />;
  }

  const legendParts: string[] = [];
  if (promote > 0 && promoteTarget) legendParts.push(`Top ${promote} promote → ${promoteTarget}`);
  if (relegate > 0 && relegateTarget) legendParts.push(`Bottom ${relegate} drop → ${relegateTarget}`);
  else if (relegate > 0) legendParts.push(`Bottom ${relegate} relegate`);

  return (
    <div className="overflow-hidden rounded-[20px] border border-black/[0.07] bg-white shadow-[0_12px_28px_-20px_rgba(20,20,50,0.28)]">
      <div className="border-b border-black/[0.06] bg-fill/35 px-4 py-3">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[14px] font-extrabold tracking-[-0.01em] text-ink">Full division table</span>
          <span className="num text-[11px] font-semibold text-ink3">{rows.length} players</span>
        </div>
        {legendParts.length > 0 && (
          <p className="mt-1 text-[11px] font-medium leading-[1.4] text-ink2">{legendParts.join(' · ')}</p>
        )}
      </div>

      <div className="grid grid-cols-[28px_1fr_auto] gap-2.5 border-b border-black/[0.05] px-3 py-2 text-[10px] font-bold uppercase tracking-[0.06em] text-ink3">
        <span className="text-center">#</span>
        <span>Player</span>
        <span className="min-w-[44px] text-right">Pts</span>
      </div>

      <div className="py-1">
        {rows.map((row, i) => {
          const prev = rows[i - 1];
          const zone = zoneForRank(row.rankInGroup, size);
          const showPromoCut =
            promote > 0 && prev != null && prev.rankInGroup <= promote && row.rankInGroup > promote;
          const showRelegCut =
            relegate > 0 &&
            prev != null &&
            prev.rankInGroup <= size - relegate &&
            row.rankInGroup > size - relegate;

          return (
            <div key={row.userId}>
              {showPromoCut && <ZoneCutLine kind="promotion" count={promote} />}
              {showRelegCut && <ZoneCutLine kind="relegation" count={relegate} />}
              <DivisionTableRow row={row} zone={zone} isYou={row.userId === uid} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
